import { GetStaticPaths, GetStaticProps } from "next";
import React from "react";
import { default as validateUuid } from "uuid-validate";
import ProductTemplate, {
  ProductTemplateProps,
} from "../../../components/Templates/Product";
import { GET_PRODUCT_COLLECTIONS } from "../../../components/Templates/Product/Parts/CollectionLinks";
import {
  addApolloState,
  initializeApollo,
} from "../../../graphql/apolloClient";
import {
  Currency,
  GetAllProductOptionsDocument,
  GetAllProductOptionsQuery,
  GetAllProductOptionsQueryVariables,
  GetProductCollectionsQuery,
  GetProductDocument,
  GetProductQuery,
  GetProductQueryVariables,
  Iso639,
} from "../../../graphql/operations";
import { CustomError } from "../../../utils/Error";
import Custom404 from "../../404";

type Props = {
  id: string;
  queryResult?: ProductTemplateProps;
};

type Params = {
  id: string;
};

const ProductShowPage: React.FC<Props> = ({ queryResult }) => {
  if (!queryResult.data.product) return <Custom404 />;

  return <ProductTemplate data={queryResult.data} />;
};

export default ProductShowPage;

const revalidate =
  process.env.ENVIRONMENT == "production"
    ? 86400
    : process.env.ENVIRONMENT == "development"
    ? 1
    : 60;

export const getStaticProps: GetStaticProps<Props, Params> = async (
  context
) => {
  try {
    if (!validateUuid(context.params.id)) {
      const error: CustomError = new Error(
        `${context.params.id} is not a valid UUID`
      );
      error.code = "INVALID_UUID";
      throw error;
    }

    const apolloClient = initializeApollo();

    const product = await apolloClient.query<
      GetProductQuery,
      GetProductQueryVariables
    >({
      query: GetProductDocument,
      variables: {
        id: context.params.id,
        currency: "USD" as Currency,
        lang: Iso639.En,
      },
    });

    const productOptions = (
      await apolloClient.query<
        GetAllProductOptionsQuery,
        GetAllProductOptionsQueryVariables
      >({
        query: GetAllProductOptionsDocument,
      })
    ).data.products.records.sort((a, b) =>
      a.translation.name.localeCompare(b.translation.name)
    );

    const productCityCollections = (
      await apolloClient.query<GetProductCollectionsQuery>({
        query: GET_PRODUCT_COLLECTIONS,
        variables: {
          input: {
            locationId: product.data.product.location.id,
          },
        },
      })
    ).data.productCollections.records;

    const productCountryCollections = (
      await apolloClient.query<GetProductCollectionsQuery>({
        query: GET_PRODUCT_COLLECTIONS,
        variables: {
          input: {
            locationId: product.data.product.location.country.id,
          },
        },
      })
    ).data.productCollections.records;

    return addApolloState(apolloClient, {
      props: {
        id: context.params.id,

        queryResult: {
          data: {
            product: product.data.product,
            productOptions: productOptions,
            productCityCollections: productCityCollections,
            productCountryCollections: productCountryCollections,
          },
        },
      } as Props,
      revalidate: revalidate,
    });
  } catch (error) {
    console.error(error);

    const result =
      error.code === "INVALID_UUID" ? { data: { product: null } } : null;

    return {
      props: {
        id: context.params.id,
        queryResult: result,
      },
      revalidate: 1,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const apolloClient = initializeApollo();
    const result = await apolloClient.query<
      GetAllProductOptionsQuery,
      GetAllProductOptionsQueryVariables
    >({
      query: GetAllProductOptionsDocument,
    });
    const paths = result.data.products.records.map((r) => ({
      params: { id: r.id },
    }));
    return { paths, fallback: "blocking" };
  } catch {
    return { paths: [], fallback: "blocking" };
  }
};
